import {Fragment, ReactNode, useEffect} from "react"
import {Navigate, useSearchParams} from "react-router-dom"
import {CircularProgress, Stack, Typography} from "@mui/joy"
import {LockKeyhole} from "lucide-react"
import Button from "../Button"
import Card from "../Card"
import {useAuth} from "../../hooks/useAuth"
import {useTranslation} from "react-i18next"

interface AuthGuardProps {
    children: ReactNode
    /** Shown in the dialog to explain why this screen asks for an account */
    reason?: string
    /** Keeps a signed in visitor away from the auth screen */
    reverseAuth?: boolean
}

/**
 * Gate around routes that need an account.
 *
 * The visitor is asked to sign in where they are instead of being sent to another screen: the
 * route stays, so the page renders as soon as the session lands, with nothing to navigate back to.
 *
 * Nothing is decided while the stored Firebase session is still loading, since redirecting on the
 * first render would bounce a signed in visitor on every refresh.
 */
const AuthGuard = ({children, reason, reverseAuth}: AuthGuardProps) => {
    const auth = useAuth()
    const {t} = useTranslation()
    const [searchParams] = useSearchParams()

    const locked = !reverseAuth && !auth.loading && !auth.user

    useEffect(() => {
        if (locked)
            auth.requestSignIn?.(reason)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locked])

    if (auth.loading)
        return <Stack height={"100dvh"} alignItems={"center"} justifyContent={"center"}
                      sx={{backgroundColor: "#050e1c"}}>
            <CircularProgress/>
        </Stack>

    if (reverseAuth)
        // Back to the page that asked for a sign in, when there was one
        return auth.user ? <Navigate to={searchParams.get("next") || "/analyze"} replace/> : <Fragment>{children}</Fragment>

    /**
     * Behind the dialog, and what is left once it is dismissed: the screen keeps its place in the
     * app rather than throwing the visitor out of it.
     */
    if (!auth.user)
        return <Stack flex={1} alignItems={"center"} justifyContent={"center"} padding={{xs: 2, md: 4}} width={"100%"}
                      boxSizing={"border-box"}>
            <Card sx={{maxWidth: "22rem", alignItems: "center", textAlign: "center"}}>
                <LockKeyhole size={32}/>
                <Typography level={"title-lg"}>{t("guardTitle")}</Typography>
                <Typography level={"body-sm"}>
                    {reason ? t(reason) : t("guardDefaultReason")}
                </Typography>
                <Button onClick={() => auth.requestSignIn?.(reason)}>{t("signIn")}</Button>
            </Card>
        </Stack>

    return <Fragment>{children}</Fragment>
}

export default AuthGuard
